<template>
  <form
    class="conversation--form"
    @submit.prevent="onFormSubmit"
    @keydown="handleFormKeydown"
  >
    <div>
      <div class="row">
        <div class="columns">
          <label>
            {{ $t('NEW_CONVERSATION.FORM.INBOX.LABEL') }}
            <input :value="targetInbox.name" type="text" disabled />
          </label>
        </div>
        <div class="columns" style="margin-inline-start: 1rem; flex: 0.8">
          <label :class="{ error: $v.toEmail.$error }">
            {{ $t('CONTACT_FORM.FORM.EMAIL_ADDRESS.LABEL') }}
            <contact-input-search
              :email.sync="toEmail"
              :placeholder="$t('CONTACT_FORM.FORM.EMAIL_ADDRESS.PLACEHOLDER')"
              field="email"
              @on-select-contact="onSelectContact"
              @input="setEmail"
            />
            <span v-if="$v.toEmail.$error" class="message">
              {{ $t('CONTACT_FORM.FORM.EMAIL_ADDRESS.ERROR') }}
            </span>
          </label>
        </div>
        <div class="columns" style="margin-inline-start: 1rem; flex: 1.2">
          <label>
            {{ $t('CONTACT_FORM.FORM.NAME.LABEL') }}
            <input
              v-model.trim="contactName"
              type="text"
              :placeholder="$t('CONTACT_FORM.FORM.NAME.PLACEHOLDER')"
            />
          </label>
        </div>
      </div>
      <div class="row">
        <div class="columns">
          <div class="w-full">
            <label :class="{ error: $v.subject.$error }">
              {{ $t('NEW_CONVERSATION.FORM.SUBJECT.LABEL') }}
              <input
                v-model="subject"
                type="text"
                :placeholder="$t('NEW_CONVERSATION.FORM.SUBJECT.PLACEHOLDER')"
                @input="$v.subject.$touch"
              />
              <span v-if="$v.subject.$error" class="message">
                {{ $t('NEW_CONVERSATION.FORM.SUBJECT.ERROR') }}
              </span>
            </label>
          </div>
          <div class="w-full">
            <label>
              {{ $t('NEW_CONVERSATION.FORM.MESSAGE.LABEL') }}
            </label>
            <reply-email-head
              :cc-emails.sync="ccEmails"
              :bcc-emails.sync="bccEmails"
            />
            <div class="editor-wrap">
              <email-woot-message-editor
                v-model="message"
                class="message-editor"
                :class="{ editor_warning: $v.message.$error }"
                :enable-variables="true"
                :signature="signatureToApply"
                :allow-signature="true"
                :placeholder="$t('NEW_CONVERSATION.FORM.MESSAGE.PLACEHOLDER')"
                :update-selection-with="updateEditorSelectionWith"
                @clear-selection="clearEditorSelection"
                @blur="$v.message.$touch"
              />
              <span v-if="$v.message.$error" class="editor-warning__message">
                {{ $t('NEW_CONVERSATION.FORM.MESSAGE.ERROR') }}
              </span>
            </div>
          </div>
          <div class="flex flex-col">
            <div class="flex items-center mt-1 mb-1 gap-2">
              <woot-button
                v-if="isAnEmailChannel"
                type="button"
                :title="$t('CONVERSATION.REPLYBOX.TIP_EMOJI_ICON')"
                icon="emoji"
                emoji="😊"
                color-scheme="secondary"
                variant="smooth"
                size="small"
                @click="toggleEmojiPicker"
              />
              <emoji-input
                v-if="showEmojiPicker"
                v-on-clickaway="hideEmojiPicker"
                :class="emojiDialogClassOnRTLView"
                :on-click="emojiOnClick"
              />
              <woot-button
                v-if="showDocalButton"
                icon="document"
                color-scheme="secondary"
                variant="smooth"
                size="small"
                :disabled="!isDocalContactValid"
                @click="isDocalContactValid && toggleDocal()"
              />
              <docal-documents-modal
                :show="showDocalModal"
                :contact-email="toEmail"
                :inbox-id="targetInbox.id"
                @signing-link="onDocalSigningLink"
                @cancel="showDocalModal = false"
              />
              <file-upload
                ref="uploadAttachment"
                input-id="forwardEmailAttachment"
                :size="4096 * 4096"
                :accept="allowedFileTypes"
                :multiple="true"
                :drop="true"
                :drop-directory="false"
                :data="{
                  direct_upload_url: '/rails/active_storage/direct_uploads',
                  direct_upload: true,
                }"
                @input-file="
                  file => fileUploadHandler(file, { isMailboxFileUpload: true })
                "
              >
                <woot-button
                  class-names="button--upload"
                  icon="attach"
                  emoji="📎"
                  color-scheme="secondary"
                  variant="smooth"
                  size="small"
                >
                  {{ $t('NEW_CONVERSATION.FORM.ATTACHMENTS.SELECT') }}
                </woot-button>
                <span
                  class="text-slate-500 ltr:ml-1 rtl:mr-1 font-medium text-xs dark:text-slate-400"
                >
                  {{ $t('NEW_CONVERSATION.FORM.ATTACHMENTS.HELP_TEXT') }}
                </span>
              </file-upload>
            </div>
            <div
              v-if="hasAttachments"
              class="max-h-20 overflow-y-auto mb-4 mt-1.5"
            >
              <attachment-preview
                class="[&>.preview-item]:dark:bg-slate-700 flex-row flex-wrap gap-x-3 gap-y-1"
                :attachments="attachedFiles"
                :remove-attachment="removeAttachment"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="modal-footer">
      <button class="button clear" @click.prevent="onCancel">
        {{ $t('NEW_CONVERSATION.FORM.CANCEL') }}
      </button>
      <woot-button
        type="submit"
        :is-loading="conversationsUiFlags.isCreating"
        :is-disabled="!isAbleToSendMsg"
      >
        {{ $t('NEW_CONVERSATION.FORM.SUBMIT') }}
      </woot-button>
    </div>
    <div class="row">
      <label>
        <input v-model="isAssigneeMe" type="checkbox" />
        {{ $t('NEW_CONVERSATION.FORM.ASSIGNEE_ME_LABEL') }}
      </label>
    </div>
  </form>
</template>

<script>
import { mapGetters } from 'vuex';
import { required } from 'vuelidate/lib/validators';
import EmailWootMessageEditor from 'dashboard/components/widgets/WootWriter/EmailEditor/Index.vue';
import ReplyEmailHead from 'dashboard/components/widgets/conversation/ReplyEmailHead';
import ContactInputSearch from 'dashboard/modules/contact/components/ContactInputSearch.vue';
import FileUpload from 'vue-upload-component';
import AttachmentPreview from 'dashboard/components/widgets/AttachmentsPreview';
import { ALLOWED_FILE_TYPES } from 'shared/constants/messages';
import { ExceptionWithMessage } from 'shared/helpers/CustomErrors';
import { validEmailsByComma } from 'dashboard/components/widgets/conversation/helpers/emailHeadHelper';
import { appendSignature } from 'dashboard/helper/editorHelper';
import alertMixin from 'shared/mixins/alertMixin';
import fileUploadMixin from 'dashboard/mixins/fileUploadMixin';
import uiSettingsMixin from 'dashboard/mixins/uiSettings';
import DocalDocumentsModal from 'dashboard/components/widgets/conversation/DocalDocumentsModal.vue';
import EmojiInput from 'shared/components/emoji/EmojiInput';
import inboxMixin from 'shared/mixins/inboxMixin';
import rtlMixin from 'shared/mixins/rtlMixin';

export default {
  name: 'ForwardEmailForm',
  components: {
    ContactInputSearch,
    EmailWootMessageEditor,
    ReplyEmailHead,
    FileUpload,
    AttachmentPreview,
    DocalDocumentsModal,
    EmojiInput,
  },
  mixins: [alertMixin, fileUploadMixin, uiSettingsMixin, inboxMixin, rtlMixin],
  props: {
    forwardMessage: {
      type: Object,
      required: true,
    },
    inbox: {
      type: Object,
      required: true,
    },
    onSubmit: {
      type: Function,
      default: () => {},
    },
  },
  data() {
    return {
      contact: null,
      toEmail: '',
      contactName: '',
      subject: '',
      message: '',
      ccEmails: '',
      bccEmails: '',
      attachedFiles: [],
      isAssigneeMe: true,
      showDocalModal: false,
      showEmojiPicker: false,
      updateEditorSelectionWith: '',
    };
  },
  validations: {
    subject: { required },
    message: { required },
    toEmail: {
      required,
      hasValidEmail(value) {
        return validEmailsByComma(value || '');
      },
    },
  },
  computed: {
    ...mapGetters({
      conversationsUiFlags: 'contactConversations/getUIFlags',
      currentUser: 'getCurrentUser',
      currentChat: 'getSelectedChat',
      globalConfig: 'globalConfig/get',
      getMessageSignature: 'getMessageSignature',
      getAccount: 'accounts/getAccount',
      accountId: 'getCurrentAccountId',
    }),
    account() {
      return this.getAccount(this.accountId);
    },
    showDocalButton() {
      if (!this.account?.additional_attributes?.document_signature_enabled) {
        return false;
      }

      return this.isAnEmailChannel || this.isASmsInbox;
    },
    isDocalEmailValid() {
      const email = this.toEmail;
      return email && validEmailsByComma(email);
    },
    isDocalContactValid() {
      if (this.isAnEmailChannel) return this.isDocalEmailValid;
      return false;
    },
    targetInbox() {
      return this.inbox || {};
    },
    messageSignature() {
      return this.getMessageSignature(this.targetInbox.id)?.body || '';
    },
    signatureToApply() {
      return this.messageSignature;
    },
    hasAttachments() {
      return this.attachedFiles.length > 0;
    },
    allowedFileTypes() {
      return ALLOWED_FILE_TYPES;
    },
    isAbleToSendMsg() {
      return !!this.targetInbox.id;
    },
    forwardPayload() {
      const payload = {
        inboxId: this.targetInbox.id,
        contact: {
          email: this.toEmail,
          name: this.contactName || this.toEmail,
          phone_number: undefined,
        },
        message: {
          content: this.message,
          forwarded: true,
          to_emails: this.toEmail,
        },
        mailSubject: this.subject,
        newConversation: true,
        assigneeId: this.isAssigneeMe ? this.currentUser.id : undefined,
        originalConversationId: this.forwardMessage?.conversation_id,
      };

      if (this.ccEmails) {
        payload.message.cc_emails = this.ccEmails;
      }
      if (this.bccEmails) {
        payload.message.bcc_emails = this.bccEmails;
      }
      if (this.attachedFiles && this.attachedFiles.length) {
        payload.files = [];
        this.setAttachmentPayload(payload);
      }

      return payload;
    },
    emojiDialogClassOnRTLView() {
      if (this.isRTLView) {
        return 'emoji-dialog--rtl';
      }
      return '';
    },
  },
  created() {
    this.prefillFromForwardMessage();
  },
  mounted() {
    if (this.messageSignature) {
      this.message = appendSignature(this.message, this.signatureToApply);
    }
  },
  methods: {
    prefillFromForwardMessage() {
      if (!this.forwardMessage) return;
      let { from, to, originalSubject, date } = this.contentAttributes(
        this.forwardMessage
      );

      if (!to) {
        to = this.inbox.email;
      }
      if (!originalSubject) {
        originalSubject = `Re: ${this.currentChat?.additional_attributes?.mail_subject}`;
      }

      const originalContent = this.forwardMessage.content || '';

      const subjectPrefix = originalSubject.toLowerCase().startsWith('fwd: ')
        ? ''
        : 'Fwd: ';
      this.subject = originalSubject
        ? `${subjectPrefix}${originalSubject}`
        : 'Fwd: ';

      const forwardHeader =
        `\n\n---------- Forwarded message ----------\n` +
        `From: ${from}\n` +
        `Date: ${date}\n` +
        `Subject: ${originalSubject}\n` +
        `To: ${to}\n`;

      this.message = forwardHeader + originalContent;

      this.attachedFiles = (this.forwardMessage.attachments || []).map(att => {
        const fileTypeMap = {
          image: 'image/jpeg',
          video: 'video/mp4',
          audio: 'audio/mpeg',
        };
        const contentType =
          att.content_type ||
          fileTypeMap[att.file_type] ||
          'application/octet-stream';
        const filename =
          att.filename ||
          decodeURIComponent(
            (att.data_url || '').split('/').pop().split('?')[0]
          ) ||
          'attachment';
        return {
          resource: {
            ...att,
            content_type: contentType,
            filename,
            byte_size: att.file_size,
          },
          thumb: att.thumb_url || att.data_url,
          blobSignedId: undefined,
          isPrefilledForward: true,
          dataUrl: att.data_url,
        };
      });
    },
    contentAttributes(message) {
      const emailAttrs = message.content_attributes?.email || {};
      const from = message.sender?.email || '';
      const to = Array.isArray(emailAttrs.to)
        ? emailAttrs.to.join(', ')
        : emailAttrs.to || '';
      const originalSubject = emailAttrs.subject || '';
      const date = message.created_at
        ? new Date(message.created_at * 1000).toLocaleString()
        : '';

      return {
        from,
        to,
        originalSubject,
        date,
      };
    },
    escapeHtml(text) {
      if (!text) return '';
      return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    },
    setAttachmentPayload(payload) {
      this.attachedFiles.forEach(attachment => {
        if (attachment.isPrefilledForward) {
          if (attachment.dataUrl) {
            payload.files.push(attachment.dataUrl);
          }
        } else if (this.globalConfig.directUploadsEnabled) {
          payload.files.push(attachment.blobSignedId);
        } else {
          payload.files.push(attachment.resource.file);
        }
      });
    },
    attachFile({ blob, file }) {
      const reader = new FileReader();
      reader.readAsDataURL(file.file);
      reader.onloadend = () => {
        this.attachedFiles.push({
          resource: blob || file,
          thumb: reader.result,
          blobSignedId: blob ? blob.signed_id : undefined,
        });
      };
    },
    removeAttachment(itemIndex) {
      this.attachedFiles = this.attachedFiles.filter(
        (item, index) => itemIndex !== index
      );
    },
    handleFormKeydown(event) {
      if (event.key === 'Enter' && document.activeElement.type !== 'submit') {
        event.preventDefault();
      }
    },
    onCancel() {
      this.$emit('cancel');
    },
    onSelectContact(contact) {
      this.contact = contact;
      this.toEmail = contact.email;
      this.contactName = contact.name;
    },
    setEmail(value) {
      this.toEmail = value;
    },
    onFormSubmit() {
      this.createConversation(this.forwardPayload);
    },
    async createConversation(payload) {
      this.$v.$touch();
      if (this.$v.$invalid) {
        return;
      }
      try {
        const data = await this.onSubmit({
          params: payload,
          isFromWhatsApp: false,
        });
        this.$emit('forward-sent', {
          newConversationId: data.id,
          recipients: this.toEmail,
        });
        this.$router.push({
          name: 'inbox_conversation',
          params: { conversation_id: data.id },
        });
      } catch (error) {
        if (error instanceof ExceptionWithMessage) {
          this.showAlert(error.data);
        } else {
          this.showAlert(this.$t('NEW_CONVERSATION.FORM.ERROR_MESSAGE'));
        }
      }
    },
    toggleDocal() {
      this.showDocalModal = !this.showDocalModal;
    },
    onDocalSigningLink(url) {
      this.showDocalModal = false;
      const currentMsg = this.message || '';
      const separator = currentMsg.trim() ? ' ' : '';
      this.message = currentMsg + separator + url;
    },
    emojiOnClick(emoji) {
      this.updateEditorSelectionWith = emoji;
    },
    clearEditorSelection() {
      this.updateEditorSelectionWith = '';
    },
    toggleEmojiPicker() {
      this.showEmojiPicker = !this.showEmojiPicker;
    },
    hideEmojiPicker() {
      if (this.showEmojiPicker) {
        this.toggleEmojiPicker();
      }
    },
  },
};
</script>

<style scoped lang="scss">
.conversation--form {
  padding: var(--space-normal) var(--space-large) var(--space-large);

  .file-uploads {
    text-align: start;
  }
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
}

.editor-wrap {
  position: relative;
}

::v-deep .ProseMirror.ProseMirror-woot-style {
  min-height: 13.5rem;
}

.emoji-dialog {
  @apply top-[unset] bottom-40 left-24 right-[unset];
  direction: ltr;

  // d99d
  .dashboard--blank & {
    left: 0;
  }
  // .

  &::before {
    transform: rotate(90deg);
    filter: drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.08));
    @apply right-80 bottom-2 rtl:right-0 rtl:left-80;
  }
}

.emoji-dialog--rtl {
  @apply left-[unset] right-24;

  // d99d
  .dashboard--blank & {
    right: 0;
  }
  // .
  &::before {
    transform: rotate(270deg);
    filter: drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.08));

    // d99d
    .dashboard--blank & {
      left: unset !important;
      right: var(--space-normal);
    }
    // .
  }
}
</style>
